/**
 * ChartSectionHeader — uppercase section title with a small count badge
 * and an optional right-aligned "+" affordance shell.
 *
 * Matches the Flutter chart header pattern: "ALLERGIES 9" (title + count)
 * with the + icon top-right when `showPatientSummary === true`.
 *
 * For PROD-827 Slice 1 the + is rendered as DISABLED — the write surfaces
 * arrive in PR B+. Slice 1 just establishes the visible affordance.
 */

import type { ReactNode } from 'react';
import { Plus } from 'lucide-react';
import { OWN_SECTION_TITLES, useChartPageSection } from './chart-page-context';
import { V2_EYEBROW, V2_META_STRONG } from '@/shared/ui/design-v2';

export interface ChartSectionHeaderProps {
  title: string;
  count: number;
  /** When `true`, render the disabled "+" icon shell on the right.
   *  Default `true`. Pass `false` for sections that don't take direct
   *  add (Notes, Labs, Meds — encounter-side adds only). */
  showAdd?: boolean;
  /** Optional handler. When omitted, the + icon renders disabled with
   *  a tooltip indicating the write surface isn't wired yet. */
  onAdd?: () => void;
  /** Optional content slot rendered to the LEFT of the "+" affordance.
   *  Use for section-specific links (e.g. Immunizations' "Vaccine
   *  Chart" link that opens the timeline modal). Keep visually small
   *  (text-link or icon button); the slot inherits header alignment. */
  extra?: ReactNode;
}

export function ChartSectionHeader({
  title,
  count,
  showAdd = true,
  onAdd,
  extra,
}: ChartSectionHeaderProps) {
  const addEnabled = showAdd && typeof onAdd === 'function';
  // PROD-2743 design sheet §2.6 (R5 H1), one file for all 11 chart tabs: ON = the DS caps label
  // (`section-eyebrow`, 12 bold uppercase, text-secondary) with the count as `meta-strong`, and the
  // DS `add` glyph at the 16px inline step. The look never depends on a switch.
  // Inside the one-page chart the page draws each section's title (the full name, section role). A heading that
  // IS the section's name keeps only its actions, drawn at the right of that title; any other heading (Active
  // problems, Lab results…) is a sub-heading: body strong, the count as meta. Outside the page: the header below.
  const pageSection = useChartPageSection();
  const patientPages = pageSection !== null;
  if (patientPages && OWN_SECTION_TITLES[pageSection]?.includes(title)) {
    if (!addEnabled && !extra) return null;
    return (
      <div data-slot="chart-section-actions">
        {extra}
        {addEnabled ? <ChartSectionAdd label={`Add ${title.toLowerCase()}`} onClick={onAdd} /> : null}
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between mb-2">
      {patientPages ? (
        <h3 className="m-0 text-sm font-medium text-text-primary" data-testid="chart-section-header-pages">
          {title}{' '}
          <span className="text-xs font-normal text-text-muted">· {count}</span>
        </h3>
      ) : (
        <h2 className={`m-0 ${V2_EYEBROW}`} data-testid="chart-section-header-v2">
          {title}{' '}
          <span className={V2_META_STRONG}>· {count}</span>
        </h2>
      )}
      <div className="flex items-center gap-3">
        {extra}
        {showAdd && patientPages ? (
          <ChartSectionAdd label={`Add ${title.toLowerCase()}`} onClick={onAdd} disabled={!addEnabled} />
        ) : showAdd ? (
          <button
            type="button"
            onClick={onAdd}
            disabled={!addEnabled}
            aria-disabled={!addEnabled}
            aria-label={
              addEnabled
                ? `Add ${title.toLowerCase()}`
                : `Add ${title.toLowerCase()} (coming soon)`
            }
            // The app icon button (icon pass, 16 Oct; was a 24 round blue control).
            className={addEnabled ? 'icon-action' : 'icon-action opacity-50 cursor-not-allowed'}
          >
            <Plus className="size-4" />
          </button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The one add action of a chart section on the one-page chart (the Allergies section included): a 32 icon button on
 * the section's title row, the Lucide plus at 16, named by its `aria-label` and the app-wide instant tooltip (no native
 * `title`). Look: the bridge's "patient chart" block (`data-slot="chart-section-add"`).
 */
export function ChartSectionAdd({
  label,
  onClick,
  disabled = false,
}: {
  label: string;
  onClick?: () => void;
  /** While the section's editor is open (one surface at a time). */
  disabled?: boolean;
}) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} aria-label={label} data-slot="chart-section-add">
      <Plus aria-hidden />
    </button>
  );
}
